import { connectWithHammock, ENDPOINTS } from 'hammock';
import React, { useMemo } from 'react';

import { FilterTypes } from '@candu-ai/analytics/search';
import { QueryId, QueryParamUrlObject } from '@candu-ai/analytics/types';
import { CollapsableTable } from '@candu-ai/analytics/ui';
import { TableLinkWithContentPreview } from '@candu-ai/document/preview';
import { ACTION_COLUMN } from 'canoe/components/Table/types';
import { useAuthentication, useWorkspacePath } from 'commons/hooks';
import { getLocalisedNumber } from 'commons/utils';
import { contentAnalyticsPath } from 'dashboard/pages/Content';

import { TableFilterActions } from './TableFilterActions';

interface TableProps {
  queryId: QueryId;
  queryParams: QueryParamUrlObject;
  content: any[];
  title: string;
}

const ByContentTableImpl = ({ queryId, queryParams, content, title }: TableProps) => {
  const { applicationId } = useAuthentication();
  const workspacePath = useWorkspacePath();
  const nameLookup = useMemo(
    () =>
      Object.fromEntries(content.map((c: any) => [c.id, { name: c.name, type: c.contentType }])),
    [content],
  );

  const columns = [
    {
      key: 'contentId',
      title: 'Content',
      colSpan: 6,
      render: (_: string, record: any) => {
        let text = '';

        if (nameLookup[record.content_id]) {
          const { name, type } = nameLookup[record.content_id];
          text = `${type}: ${name}`;
        }

        return (
          <TableLinkWithContentPreview
            contentId={record.content_id}
            applicationId={applicationId}
            text={text}
            link={workspacePath(contentAnalyticsPath(record.content_id))}
          />
        );
      },
    },
    {
      key: 'count',
      title,
      render: (value: number) => getLocalisedNumber(value, 'en-US'),
    },
    {
      key: 'total',
      title: 'Percentage',
      render: (text: string, record: any) =>
        `${((record.count * 100.0) / record.total).toFixed(1)}%`,
    },
    {
      key: ACTION_COLUMN,
      title: 'actions',
      render: (text: string, record: any) => (
        <TableFilterActions filterType={FilterTypes.ContentId} value={record.content_id} />
      ),
    },
  ];

  return (
    <CollapsableTable
      queryId={queryId}
      label={`${title} by Content`}
      queryParams={queryParams}
      columns={columns}
    />
  );
};

const ByContentTable = connectWithHammock({
  endpoint: ENDPOINTS.contentMetadata,
  resourceName: 'content',
})(ByContentTableImpl);

interface Props {
  queryParams: QueryParamUrlObject;
}

export const ViewsByContentTable = ({ queryParams }: Props) => (
  <ByContentTable queryId={QueryId.ViewsByContentIdTable} queryParams={queryParams} title="Views" />
);

export const InteractionsByContentTable = ({ queryParams }: Props) => (
  <ByContentTable
    queryId={QueryId.InteractionsByContentIdTable}
    queryParams={queryParams}
    title="Interactions"
  />
);

export const ChecklistsCompletedByContentTable = ({ queryParams }: Props) => (
  <ByContentTable
    queryId={QueryId.ChecklistsCompletedByContentIdTable}
    queryParams={queryParams}
    title="Checklists Completed"
  />
);

export const FormsSubmittedByContentTable = ({ queryParams }: Props) => (
  <ByContentTable
    queryId={QueryId.FormsSubmittedByContentIdTable}
    queryParams={queryParams}
    title="Forms Submitted"
  />
);
